'use client';

import Icon from '@pixum/icon';
import Text, { TextTag, TextVariant } from '@pixum/text';
import type { ChildCartArticleType } from 'services/types';
import { type LocaleIso } from 'utils';
import { useCartStore, type CartStore } from 'stores/cart';
import styles from './ArticleDetails.module.css';
import { ArticleInactiveLabel } from '../ArticleInactiveLabel';
import { ChildArticleAmount } from '../ChildArticleAmount/ChildArticleAmount';

export interface ArticleDetailsProps {
  constants: Record<string, string>;
  articleId: number;
  childArticles: ChildCartArticleType[];
  locale: LocaleIso;
  quantity: number;
  unitPrice: string;
  /** Whether the details section is expanded on initial render. Defaults to true. */
  defaultOpen?: boolean;
}

export function ArticleDetails({
  constants,
  articleId,
  childArticles,
  locale,
  quantity,
  unitPrice,
  defaultOpen = true
}: ArticleDetailsProps) {
  const liveQuantity = useCartStore((s: CartStore) => s.articleQuantities[articleId]);
  const currentQuantity = liveQuantity ?? quantity;
  const showDetailsLabel = constants['i18n_cart_show_details'];
  const hideDetailsLabel = constants['i18n_cart_hide_details'];

  if (childArticles.length === 0 && currentQuantity <= 1) return null;

  const inactiveLabelText = constants['i18n_cart_not_available'];
  const unitPriceLabel = constants['i18n_cart_product_unit_price'];

  return (
    <details className={styles['details']} open={defaultOpen}>
      <summary className={styles['toggle']}>
        <span className={styles['showLabel']}>{showDetailsLabel}</span>
        <span className={styles['hideLabel']}>{hideDetailsLabel}</span>
        <Icon className={styles['icon']} name="chevron-down" ariaHidden />
      </summary>
      <ul className={styles['priceList']}>
        <li className={styles['priceRow']}>
          <Text tag={TextTag.SPAN} variant={TextVariant.BODY} className={styles['priceLabel']}>
            {unitPriceLabel}
          </Text>
          <Text tag={TextTag.SPAN} variant={TextVariant.BODY} className={styles['priceValue']}>
            {unitPrice}
          </Text>
        </li>
        {childArticles.map(child => {
          const isInactive = child.product.status === 'inactive';

          return (
            <li key={child.id} className={styles['priceRow']}>
              <Text tag={TextTag.SPAN} variant={TextVariant.BODY} className={styles['priceLabel']}>
                {child.product.productShortTitle}
              </Text>
              {isInactive ? (
                <ArticleInactiveLabel className={styles.inactiveLabel} text={inactiveLabelText} />
              ) : (
                <ChildArticleAmount
                  childId={child.id}
                  quantity={child.quantity}
                  summaryPriceGross={child.summaryPriceGross}
                  locale={locale}
                  className={styles['priceValue']}
                />
              )}
            </li>
          );
        })}
      </ul>
    </details>
  );
}
